import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobFamily } from '~/shared/data-contract/job-family';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';
import { updateFamiliesWithClustersCache } from './use-families-with-clusters';

export type UsePatchJobFamilyParams = {
    readonly jobFamily: JobFamily;
};

export type UsePatchJobFamilyResult = UseMutationResult<JobFamily, Error, UsePatchJobFamilyParams>;

export const usePatchJobFamily = (): UsePatchJobFamilyResult => {
    const { patchJobFamily } = useApplicationFetchers();
    const { applicationState } = useContext(ApplicationContext);

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobFamily }: UsePatchJobFamilyParams) => {
            const result = await patchJobFamily({
                jobFamily,
                languageCode: applicationState.language.code,
            });
            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: (params) => {
            const { architectureId } = params.jobFamily;
            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(architectureId),
            });

            updateFamiliesWithClustersCache(queryClient, architectureId, (old) =>
                old.map((family) =>
                    family.id === params.jobFamily.id
                        ? { ...family, translations: params.jobFamily.translations }
                        : family,
                ),
            );
        },
        onSuccess: (_, { jobFamily }) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.familiesWithClusters(jobFamily.architectureId),
            });
        },
        throwOnError: false,
    });
};
